// Today: the job on today's list (address, time, product, m², day n of m), the
// work order and day completion, any job the office
// is offering, and what tomorrow holds.
import { useCallback } from "react";
import { Link } from "react-router-dom";
import { Camera, ChevronRight, Clock, MapPin, Package } from "lucide-react";
import { Button } from "@aiw/ui";
import type { Verdict } from "@artemis-group/aiw-contracts";
import { useT } from "../i18n";
import { usePageHead } from "../shell/Frame";
import { useRole } from "../hooks/useMe";
import { useCases } from "../hooks/useCases";
import { useJobInstalls, useMyJobs, useWorkOrder } from "../hooks/useReads";
import { day, isSameDay, m2, time, todayLong } from "../lib/format";
import { useFileReport } from "../lib/report";
import { installationComplete, type InstallerJob } from "../lib/types";
import { sydneyToday, sydneyStartOfDay, shiftSydneyDay } from "@artemis-group/aiw-contracts";
import { useReportActions } from "../state/reports";
import { JobCard } from "../components/JobCard";
import { MyCalendar } from "../components/MyCalendar";
import { OfferCard } from "../components/OfferCard";
import { Chip, Panel } from "../components/Panel";

export function Today() {
  const fileReport=useFileReport();
  const {updateReport}=useReportActions();
  const { t, lang } = useT();
  const { partyId, canInstall, isLoading: meLoading } = useRole();
  const jobs = useMyJobs();
  const cases = useCases();

  const list = jobs.data ?? [];
  const todays = list.filter((j) => !installationComplete(j) && j.portal_state !== "offered" && isSameDay(j.install_next_date));
  const job = todays[0] ?? null;
  const installs = useJobInstalls(job?.ref);
  const wo = useWorkOrder(job?.ref);
  const days = installs.data?.[0]?.days ?? [];
  const dayIdx = Math.max(0, days.findIndex((d) => isSameDay(d.date)));
  const dayN = days.length ? dayIdx + 1 : 1;
  const dayM = days.length || 1;

  // Noon on Sydney's tomorrow — a device-local +1 day lands on the wrong date near midnight and at the daylight-saving change.
  const tomorrow = new Date(Date.parse(sydneyStartOfDay(shiftSydneyDay(sydneyToday(), 1))!) + 12 * 3600_000);
  const tomorrowDay = days[dayIdx + 1] && isSameDay(days[dayIdx + 1]!.date, tomorrow) ? days[dayIdx + 1] : null;
  const tomorrowJobs = list.filter((j) => j.ref !== job?.ref && !installationComplete(j) && j.portal_state !== "offered" && isSameDay(j.install_next_date, tomorrow));

  const date = todayLong(lang);
  usePageHead(t("title_today"), jobs.isLoading ? null : todays.length === 0 ? t("sub_today_none", { date }) : todays.length === 1 ? t("sub_today", { date, n: 1, d: dayN, m: dayM }) : t("sub_today_many", { date, n: todays.length }));

  const next = list.filter((j) => !installationComplete(j) && j.install_next_date && j.install_next_date > sydneyToday()).sort((a, b) => a.install_next_date!.localeCompare(b.install_next_date!))[0];

  if (jobs.isError) return <Panel><p role="alert" className="text-sm text-destructive">{t("work_load_failed")}</p><Button size="touch" className="mt-3" onClick={() => void jobs.refetch()}>{t("retry_work")}</Button></Panel>;

  /** Accept / Decline: the verdict on the case, then the party's answer on the job as a report. */
  const decideOffer = useCallback(async (caseId: string, decision: Verdict["decision"], extra?: { reason?: string | null }) => {
    await cases.decide(caseId, decision, extra);
    if (decision === "edit") return;
    const k = cases.cases.find((c) => c.kase.id === caseId)?.kase;
    const offered = offeredJobFor(k?.questions.what, list);
    if (!offered || !partyId) return;
    try {
      await fileReport({ job_ref: offered.ref, installation_id: offered.installation_id ?? null, party_id: partyId, kind: decision === "approve" ? "accept" : "decline", notes: extra?.reason ?? "", scope: caseId });
      updateReport(offered.ref, { offer: decision === "approve" ? "accepted" : "declined" });
    } catch { /* the verdict stands; the report is retried from My jobs */ }
  }, [cases, list, partyId]);

  return (
    <div className="grid gap-4 md:grid-cols-2 md:items-start">
      <div className="space-y-4">
        {jobs.isLoading || meLoading ? (
          <p className="text-sm text-muted-foreground">{t("loading")}</p>
        ) : job ? (
          <JobCard job={job} sub={job.address ?? job.suburb} pill={t("day_n_of_m", { n: dayN, m: dayM })} tone="info">
            <div className="flex flex-wrap gap-1.5">
              <Chip mono>{job.ref}</Chip>
              {job.area_m2 != null && <Chip>{m2(job.area_m2)}</Chip>}
              {job.product && <Chip>{job.product}</Chip>}
              {!!job.stairs && <Chip>{job.stairs} {t("stairs")}</Chip>}
            </div>
            <ul className="mt-3 space-y-1.5 text-sm">
              <li className="flex items-start gap-2"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /><span>{wo.data?.start_time ? t("start_at", { time: time(wo.data.start_time, lang) }) : t("start_time_unset")}{job.customer ? ` · ${job.customer}` : ""}</span></li>
              <li className="flex items-start gap-2"><Package className={`mt-0.5 h-4 w-4 shrink-0 ${wo.data?.delivered_at ? "text-success" : "text-muted-foreground"}`} aria-hidden /><span>{wo.data?.delivered_at ? t("materials_since", { date: day(wo.data.delivered_at, lang) }) : t("materials_status_unrecorded")}</span></li>
              {(wo.data?.access || wo.data?.parking) && <li className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /><span>{[wo.data.parking?.split(" · ")[0], wo.data.access?.split(" · ")[0]].filter(Boolean).join(" · ")}</span></li>}
            </ul>
            <div className={`mt-3 grid gap-2 ${canInstall ? "grid-cols-2" : "grid-cols-1"}`}>
              <Button asChild size="touch" className="px-2 text-sm"><Link to={`/work-order/${job.ref}`}>{t("btn_work_order")}</Link></Button>
              {canInstall && <Button asChild size="touch" variant="outline" className="px-2 text-sm"><Link to={`/work-order/${job.ref}/completion`}>{t("btn_day_complete")}</Link></Button>}
            </div>
          </JobCard>
        ) : (
          <Panel>
            <p className="font-medium">{t("no_job_today")}</p>
            {next && <p className="mt-1 text-sm text-muted-foreground"><Link className="text-primary underline-offset-2 hover:underline" to={`/work-order/${next.ref}`}>{t("next_job", { name: next.name ?? next.ref, date: day(next.install_next_date, lang) })}</Link></p>}
          </Panel>
        )}

        {todays.slice(1).map((other) => (
          <JobCard key={other.ref} job={other} sub={other.address ?? other.suburb} pill={t("pill_installing")} tone="info" to={`/work-order/${other.ref}`}>
            <Button asChild size="touch" className="mt-3 w-full"><Link to={`/work-order/${other.ref}`}>{t("btn_work_order")}</Link></Button>
          </JobCard>
        ))}

        {job && canInstall && (
          <Link to={`/work-order/${job.ref}/completion`} className="flex min-h-[56px] items-center gap-3 rounded-lg border bg-card px-4 py-3 text-sm font-medium active:bg-accent/50">
            <Camera className="h-5 w-5 shrink-0 text-primary" aria-hidden />
            <span className="flex-1">{t("biglink_complete")}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          </Link>
        )}
      </div>

      <div className="space-y-4">
        {cases.cases.length > 0 && (
          <div className="space-y-2">
            <div className="kicker">{t("new_job_offered")}</div>
            {cases.cases.map((lc) => (
              <OfferCard key={lc.kase.id} kase={lc.kase} state={lc.state} busy={lc.busy} onDecide={(d, extra) => decideOffer(lc.kase.id, d, extra)} />
            ))}
          </div>
        )}

        {/* Their own schedule as a subscription — the office issues the address, they add it once. */}
        <MyCalendar />

        <Panel title={t("tomorrow")}>
          {tomorrowDay ? (
            <p className="text-sm"><b>{job?.name}</b> · {t("day_n", { n: dayIdx + 2 })}{tomorrowDay.task ? ` · ${tomorrowDay.task}` : ""}</p>
          ) : tomorrowJobs.length ? (
            <ul className="space-y-1 text-sm">{tomorrowJobs.map((j) => <li key={j.ref}><b>{j.name}</b>{j.product ? ` · ${j.product}` : ""}</li>)}</ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t("tomorrow_none")}</p>
          )}
        </Panel>
      </div>
    </div>
  );
}

/** Which of my jobs the offer is about: the record's job_ref, else the offered job the text names. */
function offeredJobFor(what: { kind: string; fields?: Record<string, unknown>; body?: string } | undefined, jobs: InstallerJob[]): InstallerJob | null {
  if (!what) return null;
  if (what.kind === "record" && typeof what.fields?.job_ref === "string") return jobs.find((j) => j.ref === what.fields!.job_ref) ?? null;
  const body = (what.body ?? "").toLowerCase();
  const offered = jobs.filter((j) => j.portal_state === "offered");
  return offered.find((j) => (j.name ?? "").split(/[·,]/).map((s) => s.trim().toLowerCase()).filter(Boolean).every((part) => body.includes(part))) ?? (offered.length === 1 ? offered[0]! : null);
}
